import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Group, Text, Flex, ScrollArea, Button, Modal, useMantineTheme, Skeleton, Title, Box, Avatar, Badge, Portal, Overlay, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSearch, IconCheck } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import ChallengeForm from '../forms/ChallengeForm';
import { getChallenges, deleteChallenge } from '../../services/api.service';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { hasDatePassed, isTodayBetween } from '../../lib/dates';
import DeleteButton from '../buttons/DeleteButton';
import { modals } from '@mantine/modals';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const Challenges = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [challenges, setChallenges] = useState([]);
	const [isEdit, setIsEdit] = useState(false);
	const [challengeEdit, setChallengeEdit] = useState({});
	const [filteredData, setFilteredData] = useState([]);
	const [opened, { open, close }] = useDisclosure(false);
	const theme = useMantineTheme();
	const { classes } = useStyles();
	const [isDeleteLoading, toggleDeleteLoading] = useState(false);

	// On load
	useEffect(() => {
		getChallengesList();
		// eslint-disable-next-line
	}, []);

	// Get challenges
	const getChallengesList = () => {

		// Get
		getChallenges(function (response) {

			// Set challenges and remove loader
			setChallenges(response.data);
			setFilteredData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				ID
			</Th>
			<Th>
				Nombre
			</Th>
			<Th>
				Descripción
			</Th>
			<Th>
				Puntos
			</Th>
			<Th>
				Nivel
			</Th>
			<Th>
				Tipo
			</Th>
			<Th>
				Cantidad
			</Th>
			<Th>
				Inicio
			</Th>
			<Th>
				Fin
			</Th>
			<Th>
				Empresa
			</Th>
			<Th>
				Estado
			</Th>
			<Th></Th>
		</tr>
	);

	// Build rows
	const rows = filteredData.map((element) => (
		<tr
			style={{ cursor: 'pointer' }}
			key={element.id}
			onClick={(event) => toggleChallengeEdit(event, element)}
		>
			<td width={100}>
				{element.id}
			</td>
			<td width={120}>
				{element.name}
			</td>
			<td>
				{element.description}
			</td>
			<td>
				{element.points}
			</td>
			<td>
				{element.level?.id}
			</td>
			<td>
				{element.challengeType?.id}
			</td>
			<td>
				{element.amount}
			</td>
			<td>
				{element.startDate ? dayjs(new Date(element.startDate)).format('DD-MM-YYYY HH:mm') : 'N/A'}
			</td>
			<td>
				{element.endDate ? dayjs(new Date(element.endDate)).format('DD-MM-YYYY HH:mm') : 'N/A'}
			</td>
			<td>
				{element.tenant ?
					<Group spacing={8}>
						{/* Avatar */}
						<Avatar
							src={element.tenant?.logo}
							size="sm"
						/>
						{/* Name */}
						<Text>
							{element.tenant?.name}
						</Text>
					</Group>
				: 'N/A'}
			</td>
			<td>
				{!element.startDate || !element.endDate ?
					<Badge color="red.9" variant="filled">Sin Estado</Badge>
				: hasDatePassed(element.endDate) ?
					<Badge color="green.6" variant="filled">Finalizado</Badge>
				: isTodayBetween(element.startDate, element.endDate) ?
					<Badge variant="filled">En curso</Badge>
				: !hasDatePassed(element.startDate) ?
					<Badge color="orange.6" variant="filled">Programado</Badge>
				: ''}
			</td>
			<td>
				<DeleteButton
					onClick={(e) => {
						e.stopPropagation();
						openDeleteModal(element.id);
					}}
				/>
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
		filterData(challenges, value);
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key]?.toString().toLowerCase().includes(query))));
	}

	// Close challenges modal
	const closeModal = () => {
		// Get challenges and close modal
		getChallengesList();
		setIsEdit(false);
		setChallengeEdit({});
		close();
	}

	// Toggle challenge edit
	const toggleChallengeEdit = (event, challenge) => {
		// Stop click and toggle edit
		event.stopPropagation();
		setIsEdit(true);
		setChallengeEdit(challenge);
		// Open modal
		open();
	}

	// Open delete confirmation modal
	const openDeleteModal = (id) => 
		modals.openConfirmModal({
			title: 'Eliminar Desafío',
			centered: true,
			children: (
				<Text size="sm">
					¿Estás seguro de querer borrar este desafío? Esta acción no se puede deshacer.
				</Text>
			),
			labels: { confirm: 'Eliminar Desafío', cancel: 'Cancelar' },
			confirmProps: { color: 'red', variant: 'light' },
			onCancel: () => {}, // Nothing
			onConfirm: () => triggerDeleteChallenge(id),
			closeOnClickOutside: false,
			zIndex: 400
		});

	// Trigger delete challenge
	const triggerDeleteChallenge = (id) => {

		// Set loader
		toggleDeleteLoading(true);

		// Call API
		deleteChallenge(id, function () {

			// Show success
			notifications.show({
				title: 'Desafío eliminado correctamente',
				color: 'green',
				icon: <IconCheck />
			});

			// Close after 1 second
			setTimeout(() => {

				// Remove preloader
				toggleDeleteLoading(false);

				// Close modal
				closeModal();

			}, 1000);

		}, function (error) {

			// Remove preloader
			toggleDeleteLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	return (
		<>
			{/* Modal */}
			<Modal
				opened={opened}
				withCloseButton={false}
				lockScroll={false}
				centered
				size="xl"
				overlayProps={{
					color: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[2],
					opacity: 0.55,
					blur: 3,
				}}
				closeOnClickOutside={false}
				closeOnEscape={false}
			>
				{/* New Challenge Form */}
				<ChallengeForm
					closeModal={closeModal}
					isEdit={isEdit}
					challenge={challengeEdit}
				/>
			</Modal>
			{/* Delete Loader */}
			{isDeleteLoading ?
				<Portal>
					<Overlay
						sx={{ zIndex: 999 }}
						center
						blur={15}
					>
						<Loader size={100} />
					</Overlay>
				</Portal>
			: null}
			{/* Content */}
			<Box className="mainContainer">
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
					className={classes.container}
				>
					{/* Title */}
					<Title mb="xl">
						Desafíos
					</Title>
					{/* Group */}
					<Flex direction="column">
						{/* Top */}
						<Flex mb="md">
							{/* New Challenge */}
							<Button
								onClick={open}
								variant="filled"
							>
								Crear Desafío
							</Button>
							{/* Search */}
							<TextInput
								placeholder="Buscar desafíos"
								icon={<IconSearch size="0.9rem" stroke={1.5} />}
								value={search}
								onChange={handleSearchChange}
								ml="sm"
								sx={{
									width: '100%',
									maxWidth: 350
								}}
								variant="filled"
							/>
						</Flex>
						{/* Table */}
						<ScrollArea>
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={7}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
				</Flex>
			</Box>
		</>
	);

}

export default Challenges;
